import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  BarChart3, Users, MessageSquare, ThumbsUp, ThumbsDown,
  TrendingUp, ArrowRight, Shield, Zap, Eye, AlertTriangle,
  ChevronDown, ChevronUp, HelpCircle
} from "lucide-react";
import { LinkedName, LinkedAvatar } from "../components/LinkedProfile";

const API = "/api";

const CATEGORY_CONFIG = {
  strengths: { label: "Strength", color: "#22c55e", bg: "bg-green-50", border: "border-green-200", text: "text-green-700", icon: Shield, barColor: "bg-green-500" },
  weaknesses: { label: "Weakness", color: "#ef4444", bg: "bg-red-50", border: "border-red-200", text: "text-red-700", icon: AlertTriangle, barColor: "bg-red-500" },
  opportunities: { label: "Opportunity", color: "#3b82f6", bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", icon: Zap, barColor: "bg-blue-500" },
  threats: { label: "Threat", color: "#f59e0b", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: Eye, barColor: "bg-amber-500" },
};

const ROLE_COLORS = {
  mentor: "bg-violet-100 text-violet-700",
  coworker: "bg-blue-100 text-blue-700",
  friend: "bg-green-100 text-green-700",
  family: "bg-amber-100 text-amber-700",
  coach: "bg-pink-100 text-pink-700",
  coachee: "bg-teal-100 text-teal-700",
  therapist: "bg-indigo-100 text-indigo-700",
  "business partner": "bg-cyan-100 text-cyan-700",
  other: "bg-slate-100 text-slate-700",
};

const ConsensusBar = ({ agree, disagree, unsure, total }) => {
  if (total === 0) return null;
  const agreePct = (agree / total) * 100;
  const disagreePct = (disagree / total) * 100;
  const unsurePct = (unsure / total) * 100;
  return (
    <div className="flex items-center gap-2 w-full">
      <span className="text-xs font-medium text-green-600 w-8 text-right">{agree}</span>
      <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden flex">
        <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${agreePct}%` }} />
        <div className="h-full bg-slate-300 transition-all duration-500" style={{ width: `${unsurePct}%` }} />
        <div className="h-full bg-red-400 transition-all duration-500" style={{ width: `${disagreePct}%` }} />
      </div>
      <span className="text-xs font-medium text-red-500 w-8">{disagree}</span>
    </div>
  );
};

const ConsensusItem = ({ item }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg = CATEGORY_CONFIG[item.category];
  const Icon = cfg.icon;
  const hasVotes = item.total_votes > 0;

  return (
    <div
      className={`rounded-xl border ${cfg.border} ${cfg.bg} p-4 transition-all duration-200 hover:shadow-md`}
      data-testid={`consensus-item-${item.item_id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded-lg bg-white/70`}>
          <Icon className="w-4 h-4" style={{ color: cfg.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-bold uppercase tracking-wider ${cfg.text}`}>{cfg.label}</span>
            {item.is_private && (
              <span className="text-[9px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full">Private</span>
            )}
          </div>
          <p className="text-sm font-medium text-slate-800 leading-snug">{item.content}</p>

          {hasVotes ? (
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold" style={{ color: item.agreement_pct >= 50 ? "#22c55e" : "#ef4444" }}>
                    {item.agreement_pct}% agreement
                  </span>
                  <span className="text-[10px] text-slate-400">({item.total_votes} vote{item.total_votes > 1 ? "s" : ""})</span>
                </div>
                {(item.agrees.length > 0 || item.disagrees.length > 0) && (
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className="flex items-center gap-0.5 text-[10px] text-slate-500 hover:text-slate-700 transition-colors"
                    data-testid={`consensus-toggle-${item.item_id}`}
                  >
                    {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    Details
                  </button>
                )}
              </div>
              <ConsensusBar agree={item.agree_count} disagree={item.disagree_count} unsure={item.unsure_count || 0} total={item.total_votes} />

              {/* Voter avatars */}
              <div className="flex -space-x-1.5">
                {[...item.agrees, ...item.disagrees].slice(0, 6).map((v) => (
                  <LinkedAvatar key={v.voter_id} username={v.voter_username} className="block">
                    <Avatar className="h-5 w-5 border-2 border-white">
                      <AvatarImage src={v.voter_picture} alt={v.voter_name} />
                      <AvatarFallback className="text-[8px] bg-slate-200">{v.voter_name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                  </LinkedAvatar>
                ))}
                {item.total_votes > 6 && (
                  <div className="h-5 w-5 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-[8px] font-medium text-slate-500">
                    +{item.total_votes - 6}
                  </div>
                )}
              </div>

              {/* Expanded voter details */}
              {expanded && (
                <div className="mt-2 space-y-1.5 pt-2 border-t border-slate-200/50">
                  {item.agrees.map((v) => (
                    <div key={v.voter_id} className="flex items-start gap-2 text-xs">
                      <ThumbsUp className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                      <LinkedName username={v.voter_username} name={v.voter_name} className="font-medium text-slate-700">
                        {v.voter_name}
                      </LinkedName>
                      {v.comment && <span className="text-slate-500 italic">"{v.comment}"</span>}
                    </div>
                  ))}
                  {item.disagrees.map((v) => (
                    <div key={v.voter_id} className="flex items-start gap-2 text-xs">
                      <ThumbsDown className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                      <LinkedName username={v.voter_username} name={v.voter_name} className="font-medium text-slate-700">
                        {v.voter_name}
                      </LinkedName>
                      {v.comment && <span className="text-slate-500 italic">"{v.comment}"</span>}
                    </div>
                  ))}
                  {(item.unsures || []).map((v) => (
                    <div key={v.voter_id} className="flex items-start gap-2 text-xs">
                      <HelpCircle className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />
                      <LinkedName username={v.voter_username} name={v.voter_name} className="font-medium text-slate-700">
                        {v.voter_name}
                      </LinkedName>
                      {v.comment && <span className="text-slate-500 italic">"{v.comment}"</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="mt-2 text-[11px] text-slate-400 italic">No votes yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color = "violet" }) => (
  <Card className="border-0 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`p-2 rounded-lg bg-${color}-50`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-800">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
        {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

const InsightsSummaryPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all"); // all, strengths, weaknesses, opportunities, threats
  const [sortBy, setSortBy] = useState("votes"); // votes, agreement

  const fetchSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API}/insights/summary`, { credentials: "include" });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        setError(true);
        toast.error("Failed to load summary");
      }
    } catch (err) {
      setError(true);
      toast.error("Failed to load summary");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8 text-center space-y-4">
        <p className="text-slate-500">Unable to load summary data.</p>
        <Button variant="outline" size="sm" onClick={() => { setLoading(true); setError(false); fetchSummary(); }} data-testid="retry-summary-btn">
          Try Again
        </Button>
      </div>
    );
  }

  const { swot_consensus, feedback_stats } = data;
  const stats = feedback_stats;

  // Filter + sort consensus items
  const filtered = swot_consensus
    .filter((item) => filter === "all" || item.category === filter)
    .sort((a, b) => {
      if (sortBy === "votes") return b.total_votes - a.total_votes;
      return (b.agreement_pct ?? -1) - (a.agreement_pct ?? -1);
    });

  const votedItems = swot_consensus.filter((i) => i.total_votes > 0);
  const highConsensus = votedItems.filter((i) => i.agreement_pct >= 75);
  const blindSpots = votedItems.filter((i) => i.agreement_pct !== null && i.agreement_pct < 50);

  const isEmpty = swot_consensus.length === 0 && stats.total_connections === 0;

  return (
    <div className="space-y-8 max-w-5xl mx-auto" data-testid="insights-summary-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Development Insights Summary</h1>
        <p className="text-sm text-slate-500 mt-1">
          See how your network perceives your strengths, weaknesses, and growth areas
        </p>
      </div>

      {isEmpty ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="p-12 text-center space-y-4">
            <BarChart3 className="w-12 h-12 text-slate-300 mx-auto" />
            <h3 className="text-lg font-semibold text-slate-600">No insights data yet</h3>
            <p className="text-sm text-slate-400 max-w-md mx-auto">
              Start by building your SWOT analysis and inviting your network to give you insights. 
              The more feedback you collect, the richer your summary becomes.
            </p>
            <div className="flex gap-3 justify-center pt-2">
              <Button variant="default" onClick={() => navigate("/tools/swot")} data-testid="go-to-swot-btn">
                Build Your SWOT
              </Button>
              <Button variant="outline" onClick={() => navigate("/network")} data-testid="go-to-network-btn">
                Invite Network
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Feedback Stats Overview */}
          <section data-testid="feedback-stats-section">
            <h2 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-violet-600" />
              Network Feedback Stats
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={Users} label="Connections" value={stats.total_connections} color="violet" />
              <StatCard icon={MessageSquare} label="Insights Received" value={stats.completed_insights} sub={`${stats.pending_insights} pending`} color="blue" />
              <StatCard icon={TrendingUp} label="Response Rate" value={`${stats.response_rate}%`} sub={`${stats.total_insight_requests} total requests`} color="green" />
              <StatCard icon={ThumbsUp} label="SWOT Votes" value={stats.total_swot_votes} sub={`${stats.unique_voters} unique voter${stats.unique_voters !== 1 ? 's' : ''}`} color="amber" />
            </div>

            {/* Role Breakdown + Top Contributors side by side */}
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              {/* Role Breakdown */}
              {Object.keys(stats.role_breakdown).length > 0 && (
                <Card className="border-0 shadow-sm" data-testid="role-breakdown-card">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm font-semibold text-slate-700">Network by Role</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-2">
                    {Object.entries(stats.role_breakdown)
                      .sort((a, b) => b[1] - a[1])
                      .map(([role, count]) => (
                        <div key={role} className="flex items-center justify-between">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${ROLE_COLORS[role] || ROLE_COLORS.other}`}>
                            {role}
                          </span>
                          <div className="flex items-center gap-2 flex-1 mx-3">
                            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                              <div
                                className="h-full bg-violet-400 rounded-full transition-all duration-500"
                                style={{ width: `${(count / stats.total_connections) * 100}%` }}
                              />
                            </div>
                          </div>
                          <span className="text-xs font-semibold text-slate-600 w-6 text-right">{count}</span>
                        </div>
                      ))}
                  </CardContent>
                </Card>
              )}

              {/* Top Contributors */}
              {stats.top_contributors.length > 0 && (
                <Card className="border-0 shadow-sm" data-testid="top-contributors-card">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm font-semibold text-slate-700">Top Contributors</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-2.5">
                    {stats.top_contributors.map((c) => (
                      <div key={c.name} className="flex items-center gap-2.5">
                        <LinkedAvatar username={c.username} className="flex-shrink-0">
                          <Avatar className="h-7 w-7">
                            <AvatarImage src={c.picture} alt={c.name} />
                            <AvatarFallback className="text-[10px] bg-violet-100 text-violet-600">{c.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                        </LinkedAvatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-700 truncate">
                            <LinkedName username={c.username} name={c.name} testId={`top-contrib-${c.name}`}>
                              {c.name}
                            </LinkedName>
                          </p>
                          <p className="text-[10px] text-slate-400 capitalize">{c.role}</p>
                        </div>
                        <span className="text-xs font-bold text-violet-600">{c.insights_given}</span>
                        <span className="text-[10px] text-slate-400">insight{c.insights_given > 1 ? 's' : ''}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </section>

          {/* SWOT Consensus View */}
          <section data-testid="swot-consensus-section">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Shield className="w-4 h-4 text-violet-600" />
                SWOT Consensus View
                {votedItems.length > 0 && (
                  <span className="text-xs font-normal text-slate-400 ml-1">
                    {votedItems.length} item{votedItems.length > 1 ? 's' : ''} with votes
                  </span>
                )}
              </h2>
              <div className="flex items-center gap-2">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="text-xs border rounded-lg px-2 py-1.5 bg-white text-slate-600 focus:ring-1 focus:ring-violet-300 outline-none"
                  data-testid="consensus-filter"
                >
                  <option value="all">All Categories</option>
                  <option value="strengths">Strengths</option>
                  <option value="weaknesses">Weaknesses</option>
                  <option value="opportunities">Opportunities</option>
                  <option value="threats">Threats</option>
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-xs border rounded-lg px-2 py-1.5 bg-white text-slate-600 focus:ring-1 focus:ring-violet-300 outline-none"
                  data-testid="consensus-sort"
                >
                  <option value="votes">Most Votes</option>
                  <option value="agreement">Highest Agreement</option>
                </select>
              </div>
            </div>

            {/* Quick Highlights */}
            {votedItems.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                {highConsensus.length > 0 && (
                  <Card className="border-0 shadow-sm bg-green-50/50" data-testid="high-consensus-card">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ThumbsUp className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-semibold text-green-700">High Consensus ({highConsensus.length})</span>
                      </div>
                      <div className="space-y-1">
                        {highConsensus.slice(0, 3).map((item) => (
                          <div key={item.item_id} className="flex items-center gap-2 text-xs">
                            <span className="font-semibold text-green-600">{item.agreement_pct}%</span>
                            <span className="text-slate-700 truncate">{item.content}</span>
                          </div>
                        ))}
                        {highConsensus.length > 3 && (
                          <p className="text-[10px] text-green-500">+{highConsensus.length - 3} more</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {blindSpots.length > 0 && (
                  <Card className="border-0 shadow-sm bg-amber-50/50" data-testid="blind-spots-card">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Eye className="w-4 h-4 text-amber-600" />
                        <span className="text-xs font-semibold text-amber-700">Blind Spots ({blindSpots.length})</span>
                      </div>
                      <div className="space-y-1">
                        {blindSpots.slice(0, 3).map((item) => (
                          <div key={item.item_id} className="flex items-center gap-2 text-xs">
                            <span className="font-semibold text-amber-600">{item.agreement_pct}%</span>
                            <span className="text-slate-700 truncate">{item.content}</span>
                          </div>
                        ))}
                        {blindSpots.length > 3 && (
                          <p className="text-[10px] text-amber-500">+{blindSpots.length - 3} more</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Consensus Items Grid */}
            {filtered.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-3">
                {filtered.map((item) => (
                  <ConsensusItem key={item.item_id} item={item} />
                ))}
              </div>
            ) : (
              <Card className="border-dashed border-2 border-slate-200">
                <CardContent className="p-8 text-center">
                  <p className="text-sm text-slate-400">
                    {swot_consensus.length === 0
                      ? "Add items to your SWOT analysis to start collecting feedback."
                      : "No items match the selected filter."
                    }
                  </p>
                  {swot_consensus.length === 0 && (
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate("/tools/swot")} data-testid="add-swot-items-btn">
                      Go to SWOT Tool <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default InsightsSummaryPage;
